<template>
  <div class="editor relative border border-truegray-300 shadow rounded">
    <div
      v-if="editor"
      class="editor-menu items-center px-2 py-1 bg-truegray-100"
    >
      <div class="flex items-center flex-wrap">
        <!-- Headings dropdown -->
        <button
          v-if="isFeatureActive('headings')"
          type="button"
          class="editor-menu__button has-children"
          :class="{
            'is-active':
              activeDropdown === 'headings' || editor.isActive('heading'),
          }"
          @click="toggleDropdown('headings')"
        >
          <EditorIcon name="headings" title="Headings" :has-children="true" />
          <div
            v-if="activeDropdown === 'headings'"
            class="top-[105%] left-[50%] transform -translate-x-1/2 rounded-sm bg-white border border-truegray-300 flex space-x-2 p-2 absolute z-40"
          >
            <button
              v-if="isFeatureActive('h1')"
              type="button"
              class="editor-menu__button justify-center items-center"
              :class="{ 'is-active': editor.isActive('heading', { level: 1 }) }"
              @click="editor.chain().focus().toggleHeading({ level: 1 }).run()"
            >
              <EditorIcon name="h1" title="Heading 1" />
            </button>

            <button
              v-if="isFeatureActive('h2')"
              type="button"
              class="editor-menu__button justify-center items-center font-primary"
              :class="{ 'is-active': editor.isActive('heading', { level: 2 }) }"
              @click="editor.chain().focus().toggleHeading({ level: 2 }).run()"
            >
              <EditorIcon name="h2" title="Heading 2" />
            </button>

            <button
              v-if="isFeatureActive('h3')"
              type="button"
              class="editor-menu__button justify-center items-center font-primary"
              :class="{ 'is-active': editor.isActive('heading', { level: 3 }) }"
              @click="editor.chain().focus().toggleHeading({ level: 3 }).run()"
            >
              <EditorIcon name="h3" title="Heading 3" />
            </button>
          </div>
        </button>

        <!-- Divider -->
        <div class="w-[1px] h-[1.2rem] mx-2 bg-truegray-300" />

        <button
          v-if="isFeatureActive('bold')"
          type="button"
          class="editor-menu__button"
          :class="{ 'is-active': editor.isActive('bold') }"
          @click="editor.chain().focus().toggleBold().run()"
        >
          <EditorIcon name="bold" title="Bold" />
        </button>
        <button
          v-if="isFeatureActive('italic')"
          type="button"
          class="editor-menu__button"
          :class="{ 'is-active': editor.isActive('italic') }"
          @click="editor.chain().focus().toggleItalic().run()"
        >
          <EditorIcon name="italic" title="Italic" />
        </button>

        <button
          v-if="isFeatureActive('underline')"
          type="button"
          class="editor-menu__button"
          :class="{ 'is-active': editor.isActive('underline') }"
          @click="editor.chain().focus().toggleUnderline().run()"
        >
          <EditorIcon name="underline" title="Underline" />
        </button>

        <button
          v-if="isFeatureActive('strike')"
          type="button"
          class="editor-menu__button"
          :class="{ 'is-active': editor.isActive('strike') }"
          @click="editor.chain().focus().toggleStrike().run()"
        >
          <EditorIcon name="strike" title="Strikethrough" />
        </button>

        <!-- Divider -->
        <div class="w-[1px] h-[1.2rem] mx-2 bg-truegray-300" />

        <button
          v-if="isFeatureActive('text_colour')"
          type="button"
          class="editor-menu__button has-children"
          :class="{
            'is-active':
              activeDropdown === 'text_colour' || editor.isActive('textStyle'),
          }"
          @click="toggleDropdown('text_colour')"
        >
          <EditorIcon
            name="text_colour"
            title="Text Colour"
            :has-children="true"
          />
          <div
            v-if="activeDropdown === 'text_colour'"
            class="top-[105%] left-[50%] transform -translate-x-1/2 rounded-sm bg-white border border-truegray-300 flex space-x-2 p-2 absolute z-40"
          >
            <button
              type="button"
              class="bg-primary w-4 h-4 rounded-full"
              :class="{ 'is-active': editor.isActive('text_colour') }"
              @click="editor.chain().focus().setColor('var(--primary)').run()"
            ></button>

            <button
              type="button"
              class="bg-black w-4 h-4 rounded-full"
              :class="{ 'is-active': editor.isActive('text_colour') }"
              @click="editor.chain().focus().setColor('#000').run()"
            ></button>

            <button
              type="button"
              class="bg-truegray-500 w-4 h-4 rounded-full"
              :class="{ 'is-active': editor.isActive('text_colour') }"
              @click="editor.chain().focus().setColor('#737373').run()"
            ></button>

            <button
              type="button"
              class="bg-red-500 w-4 h-4 rounded-full"
              :class="{ 'is-active': editor.isActive('text_colour') }"
              @click="editor.chain().focus().setColor('#EF4444').run()"
            ></button>

            <button
              type="button"
              class="bg-blue-500 w-4 h-4 rounded-full"
              :class="{ 'is-active': editor.isActive('text_colour') }"
              @click="editor.chain().focus().setColor('#3B82F6').run()"
            ></button>

            <button
              type="button"
              class="bg-green-500 w-4 h-4 rounded-full"
              :class="{ 'is-active': editor.isActive('text_colour') }"
              @click="editor.chain().focus().setColor('#22C55E').run()"
            ></button>
          </div>
        </button>

        <button
          v-if="isFeatureActive('highlight')"
          type="button"
          class="editor-menu__button"
          :class="{ 'is-active': editor.isActive('highlight') }"
          @click="editor.chain().focus().toggleHighlight().run()"
        >
          <EditorIcon name="highlight" title="Highlight" />
        </button>

        <!-- Divider -->
        <div
          v-if="isFeatureActive('text_colour') || isFeatureActive('highlight')"
          class="w-[1px] h-[1.2rem] mx-2 bg-truegray-300"
        />

        <!-- Lists dropdown -->
        <button
          v-if="isFeatureActive('lists')"
          type="button"
          class="editor-menu__button has-children"
          :class="{
            'is-active':
              activeDropdown === 'lists' ||
              editor.isActive('bulletList') ||
              editor.isActive('orderedList'),
          }"
          @click="toggleDropdown('lists')"
        >
          <EditorIcon name="ol" title="Lists" :has-children="true" />
          <div
            v-if="activeDropdown === 'lists'"
            class="top-[105%] left-[50%] transform -translate-x-1/2 rounded-sm bg-white border border-truegray-300 flex space-x-2 p-2 absolute z-40"
          >
            <button
              v-if="isFeatureActive('bulletList')"
              type="button"
              class="editor-menu__button"
              :class="{ 'is-active': editor.isActive('bulletList') }"
              @click="editor.chain().focus().toggleBulletList().run()"
            >
              <EditorIcon name="ul" title="Bullet List" />
            </button>

            <button
              v-if="isFeatureActive('orderedList')"
              type="button"
              class="editor-menu__button"
              :class="{ 'is-active': editor.isActive('orderedList') }"
              @click="editor.chain().focus().toggleOrderedList().run()"
            >
              <EditorIcon name="ol" title="Numbered List" />
            </button>
          </div>
        </button>

        <!-- Lists dropdown -->
        <button
          v-if="isFeatureActive('alignments')"
          type="button"
          class="editor-menu__button has-children"
          :class="{
            'is-active':
              activeDropdown === 'alignments' ||
              editor.isActive({ textAlign: 'center' }) ||
              editor.isActive({ textAlign: 'right' }),
          }"
          @click="toggleDropdown('alignments')"
        >
          <EditorIcon
            name="align_right"
            title="Text Alignment"
            :has-children="true"
          />
          <div
            v-if="activeDropdown === 'alignments'"
            class="top-[105%] left-[50%] transform -translate-x-1/2 rounded-sm bg-white border border-truegray-300 flex space-x-2 p-2 absolute z-40"
          >
            <button
              v-if="isFeatureActive('alignLeft')"
              type="button"
              class="editor-menu__button"
              :class="{ 'is-active': editor.isActive({ textAlign: 'left' }) }"
              @click="handleTextAlign('left')"
            >
              <EditorIcon name="align_left" title="Align Left" />
            </button>

            <button
              v-if="isFeatureActive('alignCenter')"
              type="button"
              class="editor-menu__button"
              :class="{ 'is-active': editor.isActive({ textAlign: 'center' }) }"
              @click="handleTextAlign('center')"
            >
              <EditorIcon name="align_center" title="Align Center" />
            </button>

            <button
              v-if="isFeatureActive('alignRight')"
              type="button"
              class="editor-menu__button"
              :class="{ 'is-active': editor.isActive({ textAlign: 'right' }) }"
              @click="handleTextAlign('right')"
            >
              <EditorIcon name="align_right" title="Align Right" />
            </button>
          </div>
        </button>

        <!-- Divider -->
        <div
          v-if="isFeatureActive('blockquote')"
          class="w-[1px] h-[1.2rem] mx-2 bg-truegray-300"
        />

        <button
          v-if="isFeatureActive('blockquote')"
          type="button"
          class="editor-menu__button"
          :class="{ 'is-active': editor.isActive('blockquote') }"
          @click="editor.chain().focus().toggleBlockquote().run()"
        >
          <EditorIcon name="quote" title="Block Quote" />
        </button>
      </div>
      <div class="flex items-center flex-wrap">
        <button
          v-if="isFeatureActive('code')"
          type="button"
          class="editor-menu__button"
          :class="{ 'is-active': editor.isActive('code') }"
          @click="editor.chain().focus().toggleCode().run()"
        >
          <EditorIcon name="code" title="Code" />
        </button>

        <button
          v-if="isFeatureActive('codeBlock')"
          type="button"
          class="editor-menu__button"
          :class="{ 'is-active': editor.isActive('codeBlock') }"
          @click="editor.chain().focus().toggleCodeBlock().run()"
        >
          <EditorIcon name="code_block" title="Code Block" />
        </button>

        <button
          v-if="isFeatureActive('horizontalRule')"
          type="button"
          class="editor-menu__button"
          @click="editor.chain().focus().setHorizontalRule().run()"
        >
          <EditorIcon name="hr" title="Horizontal Rule" />
        </button>

        <!-- Divider -->
        <div class="w-[1px] h-[1.2rem] mx-2 bg-truegray-300" />

        <button
          v-if="isFeatureActive('image')"
          type="button"
          class="editor-menu__button"
          :class="{ 'is-active': editor.isActive('ImageResizable') }"
          @click="openImageModal"
        >
          <EditorIcon name="image" title="Add Image" />
        </button>

        <button
          v-if="isFeatureActive('link')"
          type="button"
          class="editor-menu__button"
          :class="{ 'is-active': editor.isActive('link') }"
          @click="openLinkModal"
        >
          <EditorIcon name="link" />
        </button>

        <button
          v-if="isFeatureActive('link')"
          type="button"
          class="editor-menu__button"
          :disabled="!editor.can().unsetLink()"
          @click="handleUnsetLink"
        >
          <EditorIcon name="remove_link" />
        </button>

        <!-- Lists dropdown -->
        <button
          v-if="isFeatureActive('tables')"
          type="button"
          class="editor-menu__button has-children"
          :class="{ 'is-active': activeDropdown === 'tables' }"
          @click="toggleDropdown('tables')"
        >
          <EditorIcon name="tables" title="Tables" :has-children="true" />
          <div
            v-if="activeDropdown === 'tables'"
            class="top-[105%] left-[50%] transform -translate-x-1/2 rounded-sm bg-white border border-truegray-300 p-2 absolute z-40"
          >
            <div class="flex space-x-2">
              <button
                v-if="isFeatureActive('table')"
                type="button"
                class="editor-menu__button"
                @click="
                  editor
                    .chain()
                    .focus()
                    .insertTable({ rows: 3, cols: 3, withHeaderRow: true })
                    .run()
                "
              >
                <EditorIcon name="add_table" title="Add Table" />
              </button>

              <button
                v-if="isFeatureActive('table')"
                type="button"
                class="editor-menu__button"
                :disabled="!editor.can().deleteTable()"
                @click="editor.chain().focus().deleteTable().run()"
              >
                <EditorIcon name="delete_table" title="Delete Table" />
              </button>
            </div>
          </div>
        </button>

        <!-- Divider -->
        <div class="w-[1px] h-[1.2rem] mx-2 bg-truegray-300" />

        <button
          v-if="isFeatureActive('undo')"
          type="button"
          class="editor-menu__button"
          :disabled="!editor.can().undo()"
          @click="editor.chain().focus().undo().run()"
        >
          <EditorIcon name="undo" title="Undo" />
        </button>

        <button
          v-if="isFeatureActive('redo')"
          type="button"
          class="editor-menu__button"
          :disabled="!editor.can().redo()"
          @click="editor.chain().focus().redo().run()"
        >
          <EditorIcon name="redo" title="Redo" />
        </button>

        <!-- Lists dropdown -->
        <!--
          <button
          v-if="isFeatureActive('wand')"
          class="editor-menu__button has-children is-highlighted rounded-sm"
          :class="{ 'is-active': activeDropdown === 'wand' || pasteOption === 'paste' || pasteOption === 'paste_without_formatting' || pasteOption === 'paste_from_email' }"
          @click="toggleDropdown('wand')"
          >
          <EditorIcon name="wand" title="Special Formatting" :isSuggested="true" :hasChildren="true"/>
          <div v-if="activeDropdown === 'wand'" class="top-[105%] left-[50%] transform -translate-x-1/2 rounded-sm bg-white border border-truegray-300 flex space-x-2 p-2 absolute z-40">
          <button
          v-if="isFeatureActive('paste_without_formatting')"
          class="editor-menu__button"
          :class="{ 'is-active': pasteOption === 'paste_without_formatting' }"
          @click="setPasteOption('paste_without_formatting')"
          >
          <EditorIcon name="paste_without_formatting" title="Paste without formatting" :isSuggested="true"/>
          </button>

          <button
          v-if="isFeatureActive('paste_from_email')"
          class="editor-menu__button"
          :class="{ 'is-active': pasteOption === 'paste_from_email' }"
          @click="setPasteOption('paste_from_email')"
          >
          <EditorIcon name="paste_from_email" title="Paste from Email" :isSuggested="true" />
          </button>

          </div>
          </button> 
        -->
      </div>
    </div>

    <InlineMenu
      :editor="editor"
      @open-image-modal="openImageModal"
      @open-link-modal="openLinkModal"
      @handle-unset-link="handleUnsetLink"
      @handle-text-align="handleTextAlign"
      @unset-table="unsetTable"
      @set-paste-option="setPasteOption"
    />
    <EditorContent :editor="editor" />
  </div>
</template>

<script>
import { EventBus } from '../../../../../js/plugins/event-bus.js'
import InlineMenu from './EditorInlineMenu.vue'
import { PasteHandler } from './EditorExtensions/paste-handler.js'
import { Editor, EditorContent } from '@tiptap/vue-2'
import EditorIcon from './EditorIcon.vue'
import Bold from '@tiptap/extension-bold'
import Blockquote from '@tiptap/extension-blockquote'
import BulletList from '@tiptap/extension-bullet-list'
import Code from '@tiptap/extension-code'
import CodeBlock from '@tiptap/extension-code-block'
import Document from '@tiptap/extension-document'
// NB: https://github.com/ueberdosis/tiptap/issues/2150
import Dropcursor from '@tiptap/extension-dropcursor'
import Focus from '@tiptap/extension-focus'
import Gapcursor from '@tiptap/extension-gapcursor'
import HardBreak from '@tiptap/extension-hard-break'
import Heading from '@tiptap/extension-heading'
import History from '@tiptap/extension-history'
import Highlight from '@tiptap/extension-highlight'
import TextStyle from '@tiptap/extension-text-style'
import { Color } from '@tiptap/extension-color'
import Italic from '@tiptap/extension-italic'
import HorizontalRule from '@tiptap/extension-horizontal-rule'
// import Image from '@tiptap/extension-image'
import ImageResizable from './EditorImage/image-resizable.js'
import Link from '@tiptap/extension-link'
import ListItem from '@tiptap/extension-list-item'
import OrderedList from '@tiptap/extension-ordered-list'
import Paragraph from '@tiptap/extension-paragraph'
// import Placeholder from '@tiptap/extension-placeholder'
import Strike from '@tiptap/extension-strike'
// import Table from '@tiptap/extension-table'
import CustomTable from './EditorTable/custom-table.js'
import TableCell from '@tiptap/extension-table-cell'
import TableHeader from '@tiptap/extension-table-header'
import TableRow from '@tiptap/extension-table-row'
import Text from '@tiptap/extension-text'
import TextAlign from '@tiptap/extension-text-align'
import Underline from '@tiptap/extension-underline'

export default {
  components: {
    EditorContent,
    EditorIcon,
    InlineMenu,
  },

  props: {
    initialContent: {
      type: String,
      default: '',
    },

    activeFeatures: {
      type: Array,
      default: () => ['bold', 'italic'],
    },

    editorClass: {
      type: String,
      default: '',
    },
  },

  data() {
    return {
      editor: null,
      html: '',
      activeDropdown: null,
      pasteOption: null,
    }
  },

  computed: {
    state() {
      const { state } = this.editor
      return state
    },

    selection() {
      return this.state?.selection
    },
  },

  watch: {
    pasteOption: {
      handler: function () {
        this.editor.setOptions({
          editorProps: {
            attributes: {
              ...this.editor.options.editorProps.attributes,
              pasteOption: this.pasteOption,
            },
          },
        })
      },

      deep: true,
    },
  },

  mounted() {
    EventBus.$on('assignResourceEditor', (resource) => {
      const src = resource.file_url

      if (src) {
        this.editor.chain().focus().setImage({ src }).run()
      }

      EventBus.$emit('showResourcesSearchModal', false)
    })

    EventBus.$on('linkDataUpdated', (attrs) => {
      if (attrs) {
        const { url: href, title } = attrs
        const { from, to } = this.selection

        if (this.editor.isActive('ImageResizable')) {
          this.editor
            .chain()
            .focus()
            .updateAttributes('ImageResizable', { link: href })
            .run()
        }

        // replace/insert current selection/empty with link/title
        this.editor
          .chain()
          .focus()
          .insertContentAt({ from, to }, title, {
            updateSelection: true,
          })
          .setTextSelection({ from, to: from + title.length })
          .setLink({ href })
          .run()

        this.editor.commands.focus()
      }
    })

    EventBus.$on('editorEventPaste', () => {
      this.setPasteOption()
    })

    this.editor = new Editor({
      editorProps: {
        attributes: {
          class: `prose min-h-editor p-2 outline-none ${this.editorClass}`,
          pasteOption: null,
        },
      },

      extensions: [
        Bold,
        Blockquote,
        BulletList,
        Code,
        CodeBlock,
        Document,
        Dropcursor,
        Focus.configure({
          className: 'has-focus',
          // focus all so we can select/focus images in tables, etc.
          mode: 'all',
        }),
        Gapcursor,
        HardBreak,
        Heading,
        History,
        Italic,
        Highlight,
        TextStyle,
        Color,
        HorizontalRule,
        // Image,
        ImageResizable,
        Link.configure({
          autolink: false,
          openOnClick: false,
        }),
        ListItem,
        OrderedList,
        Paragraph,
        // Placeholder.configure({
        //   placeholder: 'Enter post content...',
        // }),
        Strike,
        CustomTable,
        TableRow,
        TableHeader,
        TableCell,
        Text,
        TextAlign.configure({
          types: ['heading', 'paragraph', 'image', 'h1', 'h2', 'h3'],
          alignments: ['left', 'center', 'right'],
        }),
        Underline,
        PasteHandler,
      ],

      content: this.initialContent,
      onUpdate: this.onUpdate,
    })
  },

  beforeUnmount() {
    this.editor.destroy()
  },

  methods: {
    isFeatureActive(feature) {
      return this.activeFeatures.includes(feature)
    },

    handleTextAlign(type) {
      this.editor.chain().focus().setTextAlign(type).run()
    },

    openImageModal() {
      EventBus.$emit('showResourcesSearchModal', 'editorResources')
    },

    openLinkModal() {
      let data = {}

      if (this.editor.isActive('ImageResizable')) {
        const href = this.editor.getAttributes('ImageResizable').link || ''
        data = {
          originalLinkUrl: href,
          isImage: true,
        }
      } else {
        const href = this.editor.getAttributes('link').href || ''
        // if focused in the middle of a link, extend the selection to entire text
        this.editor.chain().focus().extendMarkRange('link').run()
        const title =
          this.state.doc.textBetween(
            this.selection?.from,
            this.selection?.to,
            ' ',
          ) || ''
        data = {
          originalLinkUrl: href,
          originalLinkTitle: title,
        }
      }

      EventBus.$emit('showModal', {
        action: 'editor-link-modal',
        data,
      })
    },

    handleUnsetLink() {
      if (this.editor.isActive('ImageResizable')) {
        this.editor
          .chain()
          .focus()
          .updateAttributes('ImageResizable', { link: null })
          .run()
      } else {
        this.editor.chain().focus().extendMarkRange('link').unsetLink().run()
      }
    },

    setPasteOption(type) {
      type = type === this.pasteOption ? null : type
      this.$set(this, 'pasteOption', type)
    },

    unsetTable(unset) {
      this.editor.chain().focus().updateAttributes('table', { unset }).run()
      this.onUpdate()
      // @todo get storage working for disabling/enabling table actions
      // this.editor.extensionStorage.table.isUnset
    },

    onUpdate() {
      this.html = this.editor.getHTML()
      this.$emit('update-content', this.html)
    },

    toggleDropdown(dropdown) {
      // this.editor.commands.focus()
      if (this.activeDropdown === dropdown) {
        this.activeDropdown = null
      } else {
        this.activeDropdown = dropdown
      }
    },
  },
}
</script>
